import React, {useEffect, useState} from 'react';
import {Stack, Button} from "@mui/material";
import AddVisitorForm from './AddVisitorForm';
import {
  Formik,
  Form
} from 'formik';
import addVisitorModel , {addVisitorModelType} from './addVisitorModel';
import addVisitorSchema, { IN_DRIVE_VISITOR_TYPE } from './addVisitorSchema';
import * as Yup from 'yup';
import {InviteData, usePostAddInviteMutation} from "../../redux/residentPortalApi";
import LoadingCover from '../common/LoadingCover'

type MyFormValues = addVisitorModelType

const addVisitorModelInitial = addVisitorModel.initialValues

const AddVisitorFormSchema = Yup.object().shape({...addVisitorSchema})

type Props = {
  onSuccess?: (invite: InviteData) => void;
  prefill?: Partial<MyFormValues> | null;
};

const AddVisitor: React.FC<Props> = ({ onSuccess, prefill }) => {
  const [createInvite, {isSuccess, isLoading}] = usePostAddInviteMutation();
  const [showOverlay, setShowOverlay] = useState(false);

  useEffect(() => {
    setShowOverlay(isLoading || isSuccess);
    if (isSuccess) {
      setTimeout(() => setShowOverlay(false), 3000); // hide after 3 seconds
    }
  }, [isLoading, isSuccess]);

  const initialValues: MyFormValues = {
    ...addVisitorModelInitial,
    // reinvite: same visitor's details, date/time left blank so the user picks new timing
    ...prefill,
  };

  const handleSubmit = async (values: MyFormValues) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    const toLocalDateTimeString = (v: any) => {
      if (!v) return '';
      const d: Date = v instanceof Date ? v : new Date(v);
      if (isNaN(d.getTime())) return '';
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };
    const toLocalDateString = (v: any) => {
      if (!v) return '';
      const d: Date = v instanceof Date ? v : new Date(v);
      if (isNaN(d.getTime())) return '';
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };

    const data = {
      visitorName:
        values.visitorType === IN_DRIVE_VISITOR_TYPE
          ? 'inDrive'
          : `${values.firstName} ${values.lastName}`.trim(),
      visitorWhatsApp: values.visitorWhatsApp,
      visitorEmail: values.visitorEmail,
      visitorType: values.visitorType,
      purpose: values.purpose,
      isMultiDay: values.isMultiDay,
      propertyId: values.propertyId,
      consentConfirmed: values.consent,
    } as InviteData;

    if (values.isMultiDay) {
      // For multi-day invites, send start and end dates as local date strings with no timezone
      data.startDate = toLocalDateString(values.startDate);
      data.endDate = toLocalDateString(values.endDate);
    } else {
      // For single day invites, send local datetime without timezone
      data.visitDateTime = toLocalDateTimeString(values.visitDateTime);
    }

    const result = await createInvite({inviteData: data});
    if ('data' in result && result.data && onSuccess) {
      onSuccess(result.data);
    } else if ('error' in result) {
      console.error("Error creating invite:", result.error);
    }
  }

  return (
    <React.Fragment>
      {showOverlay && <LoadingCover isSuccess={isSuccess}/>}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={AddVisitorFormSchema}
      >
        <Form>
          <Stack spacing={4} width="100%" pt={4}>
            <AddVisitorForm />
            <Button
              variant="contained"
              type="submit"
              data-cy={"createAccountSubmit"}
              sx={{
                backgroundColor: '#345678',
                borderRadius: 2,
                padding: '10px 30px',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: '#234567',
                }
              }}
            >
              Create Invite
            </Button>
          </Stack>
        </Form>
      </Formik>
    </React.Fragment>
  )
}

export default AddVisitor;
